// Post detail (PRD §3.4): full post with blocks (text, images, video), a working
// love button (§3.5), comments (§3.6), and the real-name reveal (§3.7).
// Opening the post marks it read server-side.

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { useAuth } from "../auth/AuthContext";
import { api, ApiError } from "../lib/api";
import type { Block, Comment, Person, PostDetail as Post } from "../lib/types";
import { timeAgo } from "../lib/time";
import { AppHeader } from "../components/AppHeader";
import { Avatar } from "../components/Avatar";
import { Button } from "../components/Button";
import { Prose } from "../components/Prose";
import { RevealModal } from "../components/RevealModal";

export function PostDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdminView } = useAuth();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reveal, setReveal] = useState<Person | null>(null);

  useEffect(() => {
    const postId = Number(id);
    if (!Number.isInteger(postId)) {
      setError("That post has wandered off.");
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    api
      .post(postId)
      .then(({ post }) => alive && setPost(post))
      .catch((err) => alive && setError(err instanceof ApiError ? err.message : "Couldn't load that post."))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id]);

  return (
    <div className="paper-grain min-h-dvh pb-20">
      <AppHeader />
      <main className="mx-auto w-full max-w-xl px-5 py-6">
        <button
          onClick={() => navigate(-1)}
          className="stamp mb-6 text-xs text-ink-muted hover:text-ink"
        >
          ← back to the feed
        </button>

        {loading ? (
          <div className="flex justify-center py-20">
            <span className="h-8 w-8 animate-spin rounded-full border-[3px] border-citrus/30 border-t-citrus" />
          </div>
        ) : error || !post ? (
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <span className="text-4xl">🍊</span>
            <p className="text-ink-muted">{error ?? "That post has wandered off."}</p>
            <Link to="/" className="font-semibold text-citrus-deep underline">
              Back to the feed
            </Link>
          </div>
        ) : (
          <motion.article initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            {post.status === "draft" ? (
              <span className="stamp mb-3 inline-block rounded-md border border-ink/25 px-2 py-1 text-[0.6rem] text-ink-muted">
                Draft — only you can see this
              </span>
            ) : null}

            {/* Hero: first image or video block shown large before the title */}
            <HeroBlock blocks={post.blocks} />

            <h1 className="font-display text-4xl leading-tight text-ink">{post.title}</h1>

            <div className="mt-3 flex items-center gap-2.5 text-sm text-ink-muted">
              <Avatar person={post.author} size={32} onClick={() => setReveal(post.author)} />
              <button
                onClick={() => setReveal(post.author)}
                className="font-semibold text-ink-soft hover:underline"
              >
                {post.author.handle}
              </button>
              <span>·</span>
              <span>{timeAgo(post.publishedAt ?? post.createdAt)}</span>
            </div>

            {/* Content blocks — skip the first image/video if it was used as hero */}
            <div className="mt-6 flex flex-col gap-6">
              {post.blocks
                .filter((b, i) => !(i === heroIndex(post.blocks) && b.type !== "text"))
                .map((block, i) => (
                  <BlockRenderer key={i} block={block} />
                ))}
              {post.blocks.length === 0 && (
                <p className="italic text-ink-muted">No words on this one — just the moment.</p>
              )}
            </div>

            <LoveButton post={post} onReveal={setReveal} />
            <Comments postId={post.id} onReveal={setReveal} />

            {isAdminView ? (
              <div className="mt-8">
                <Link
                  to={`/admin/posts/${post.id}/edit`}
                  className="font-semibold text-citrus-deep underline decoration-dotted underline-offset-2"
                >
                  Edit this post
                </Link>
              </div>
            ) : null}
          </motion.article>
        )}
      </main>

      <RevealModal person={reveal} onClose={() => setReveal(null)} />
    </div>
  );
}

// ── Love (PRD §3.5) ────────────────────────────────────────────────────────────

function LoveButton({ post, onReveal }: { post: Post; onReveal: (p: Person) => void }) {
  const reduce = useReducedMotion();
  const [lovedByMe, setLovedByMe] = useState(post.lovedByMe);
  const [lovers, setLovers] = useState<Person[]>(post.lovers);
  const [busy, setBusy] = useState(false);
  const [burst, setBurst] = useState(0);

  const count = lovers.length;

  async function toggle() {
    if (busy) return;
    setBusy(true);
    const next = !lovedByMe;
    setLovedByMe(next); // optimistic
    if (next && !reduce) setBurst((b) => b + 1);
    try {
      const state = next ? await api.love(post.id) : await api.unlove(post.id);
      setLovedByMe(state.lovedByMe);
      setLovers(state.lovers);
    } catch {
      setLovedByMe(!next); // revert on failure
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-10 border-t-2 border-ink/10 pt-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => void toggle()}
          aria-pressed={lovedByMe}
          aria-label={lovedByMe ? "Unlove this post" : "Love this post"}
          className={
            "inline-flex items-center gap-2 rounded-full border-2 px-4 py-2 font-semibold transition-colors " +
            (lovedByMe
              ? "border-love/40 bg-love/10 text-love"
              : "border-ink/20 text-ink-muted hover:border-ink/40 hover:text-ink")
          }
        >
          <span className="relative inline-flex">
            <motion.span
              key={burst}
              animate={reduce ? undefined : { scale: [1, 1.45, 0.9, 1] }}
              transition={{ duration: 0.4 }}
              className="inline-block text-lg leading-none"
            >
              ♥
            </motion.span>
            {burst > 0 && lovedByMe && !reduce ? <HeartBurst key={`b${burst}`} /> : null}
          </span>
          <span className="tabular-nums">{count}</span>
        </button>
        <span className="text-sm text-ink-muted">{count === 1 ? "1 love" : `${count} loves`}</span>
      </div>

      {/* Who loved — tap an icon for the real-name reveal */}
      {lovers.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {lovers.map((p) => (
            <Avatar key={p.id} person={p} size={30} onClick={() => onReveal(p)} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** A little pop of hearts on love. Purely decorative; never rendered when reduced-motion. */
function HeartBurst() {
  const bits = Array.from({ length: 6 });
  return (
    <span className="pointer-events-none absolute left-1/2 top-1/2">
      {bits.map((_, i) => {
        const angle = (i / bits.length) * Math.PI * 2;
        return (
          <motion.span
            key={i}
            initial={{ opacity: 1, x: 0, y: 0, scale: 0.5 }}
            animate={{ opacity: 0, x: Math.cos(angle) * 26, y: Math.sin(angle) * 26, scale: 1.1 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            className="absolute text-xs text-love"
          >
            ♥
          </motion.span>
        );
      })}
    </span>
  );
}

// ── Comments (PRD §3.6) ─────────────────────────────────────────────────────────

function Comments({ postId, onReveal }: { postId: number; onReveal: (p: Person) => void }) {
  const [comments, setComments] = useState<Comment[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .comments(postId)
      .then(({ comments }) => alive && setComments(comments))
      .catch(() => alive && setLoadError("Couldn't load comments."))
    return () => {
      alive = false;
    };
  }, [postId]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const body = text.trim();
    if (!body || posting) return;
    setPosting(true);
    setPostError(null);
    try {
      const { comment } = await api.addComment(postId, body);
      setComments((prev) => [...(prev ?? []), comment]);
      setText("");
    } catch (err) {
      setPostError(err instanceof ApiError ? err.message : "Couldn't post that comment.");
    } finally {
      setPosting(false);
    }
  }

  function onUpdated(updated: Comment) {
    setComments((prev) => (prev ?? []).map((c) => (c.id === updated.id ? updated : c)));
  }
  function onDeleted(commentId: number) {
    setComments((prev) => (prev ?? []).filter((c) => c.id !== commentId));
  }

  const count = comments?.length ?? 0;

  return (
    <section className="mt-10 border-t-2 border-ink/10 pt-6">
      <h2 className="font-display text-2xl text-ink">
        {count === 0 ? "Comments" : count === 1 ? "1 comment" : `${count} comments`}
      </h2>

      {comments === null ? (
        loadError ? (
          <p className="mt-3 text-sm text-ink-muted">{loadError}</p>
        ) : (
          <div className="mt-4 flex justify-center py-6">
            <span className="h-6 w-6 animate-spin rounded-full border-[3px] border-citrus/30 border-t-citrus" />
          </div>
        )
      ) : comments.length === 0 ? (
        <p className="mt-3 text-sm italic text-ink-muted">
          No comments yet — be the first to say something 🍊
        </p>
      ) : (
        <ul className="mt-4 flex flex-col gap-5">
          {comments.map((c) => (
            <CommentRow
              key={c.id}
              comment={c}
              onReveal={onReveal}
              onUpdated={onUpdated}
              onDeleted={onDeleted}
            />
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="mt-6">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Add your comment…"
          className="w-full resize-y rounded-xl border-2 border-ink/25 bg-paper-deep px-4 py-3 text-ink placeholder:text-ink-muted/70 focus:border-citrus focus:bg-paper focus:outline-none"
        />
        {postError ? <p className="mt-2 text-sm text-love">{postError}</p> : null}
        <div className="mt-2 flex justify-end">
          <Button type="submit" loading={posting} disabled={!text.trim()}>
            Post comment
          </Button>
        </div>
      </form>
    </section>
  );
}

function CommentRow({
  comment,
  onReveal,
  onUpdated,
  onDeleted,
}: {
  comment: Comment;
  onReveal: (p: Person) => void;
  onUpdated: (c: Comment) => void;
  onDeleted: (id: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function saveEdit() {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { comment: updated } = await api.editComment(comment.id, body);
      onUpdated(updated);
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save that edit.");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (busy || !confirm("Delete this comment?")) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteComment(comment.id);
      onDeleted(comment.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't delete that.");
      setBusy(false);
    }
  }

  return (
    <li className="flex gap-3">
      <Avatar person={comment.author} size={36} onClick={() => onReveal(comment.author)} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 text-sm">
          <button
            onClick={() => onReveal(comment.author)}
            className="font-semibold text-ink hover:underline"
          >
            {comment.author.handle}
          </button>
          <span className="text-ink-muted">
            {timeAgo(comment.createdAt)}
            {comment.edited ? " · edited" : ""}
          </span>
        </div>

        {editing ? (
          <div className="mt-1.5">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              maxLength={2000}
              className="w-full resize-y rounded-xl border-2 border-ink/25 bg-paper-deep px-3 py-2 text-ink focus:border-citrus focus:bg-paper focus:outline-none"
            />
            <div className="mt-2 flex items-center gap-2">
              <Button onClick={() => void saveEdit()} loading={busy} disabled={!draft.trim()}>
                Save
              </Button>
              <button
                onClick={() => {
                  setEditing(false);
                  setDraft(comment.body);
                  setError(null);
                }}
                className="text-sm font-semibold text-ink-muted hover:text-ink"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="mt-1 whitespace-pre-wrap break-words text-ink-soft">{comment.body}</p>
        )}

        {error ? <p className="mt-1 text-xs text-love">{error}</p> : null}

        {!editing && (comment.mine || comment.canDelete) ? (
          <div className="mt-1.5 flex gap-3 text-xs font-semibold text-ink-muted">
            {comment.mine ? (
              <button onClick={() => setEditing(true)} className="hover:text-ink">
                Edit
              </button>
            ) : null}
            {comment.canDelete ? (
              <button
                onClick={() => void remove()}
                disabled={busy}
                className="text-love/70 hover:text-love disabled:opacity-50"
              >
                Delete
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  );
}

// ── Post media rendering (unchanged) ────────────────────────────────────────────

/** Index of the first image or video block, or -1 if none. */
function heroIndex(blocks: Block[]): number {
  return blocks.findIndex((b) => b.type === "image" || b.type === "video");
}

/** Renders the first image/video as a full-bleed hero above the title. */
function HeroBlock({ blocks }: { blocks: Block[] }) {
  const i = heroIndex(blocks);
  if (i === -1) return null;
  const block = blocks[i]!;

  if (block.type === "image") {
    if (!block.url) return null;
    return <HeroImage src={block.url} />;
  }

  // R2-hosted clip → native <video>; legacy Stream clip → iframe.
  if (block.url) {
    return (
      <div className="-mx-5 mb-5 overflow-hidden bg-black sm:mx-0 sm:mb-6 sm:rounded-2xl">
        <video
          src={block.url}
          poster={block.thumbnailUrl ?? undefined}
          controls
          playsInline
          className="block max-h-[70vh] w-full bg-black"
        />
      </div>
    );
  }

  return (
    <div className="-mx-5 mb-5 overflow-hidden bg-black sm:mx-0 sm:mb-6 sm:rounded-2xl">
      <div className="aspect-video w-full">
        <iframe
          src={`https://iframe.videodelivery.net/${block.streamUid}`}
          title="Video"
          className="h-full w-full"
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
}

function HeroImage({ src }: { src: string }) {
  const [error, setError] = useState(false);

  if (error) {
    return (
      <div className="-mx-5 mb-5 overflow-hidden sm:mx-0 sm:mb-6 sm:rounded-2xl">
        <div className="flex min-h-[260px] items-center justify-center bg-paper-deep sm:min-h-[360px]">
          <div className="flex flex-col items-center gap-3 text-center">
            <span className="text-5xl">🖼️</span>
            <p className="text-sm text-ink-muted">Cover image couldn't load</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <figure className="-mx-5 mb-5 overflow-hidden sm:mx-0 sm:mb-6 sm:rounded-2xl">
      <img
        src={src}
        alt=""
        className="block w-full"
        onError={() => setError(true)}
        loading="eager"
      />
    </figure>
  );
}

function BlockRenderer({ block }: { block: Block }) {
  if (block.type === "text") {
    return <Prose text={block.text ?? ""} />;
  }

  if (block.type === "image") {
    if (!block.url) return null;
    return <PostImage src={block.url} />;
  }

  if (block.type === "video") {
    // R2-hosted clip → native <video>; legacy Stream clip → iframe.
    if (block.url) {
      return (
        <div className="overflow-hidden rounded-2xl border-2 border-ink/10 bg-black">
          <video
            src={block.url}
            poster={block.thumbnailUrl ?? undefined}
            controls
            playsInline
            className="block max-h-[70vh] w-full bg-black"
          />
        </div>
      );
    }
    return (
      <div className="overflow-hidden rounded-2xl border-2 border-ink/10 bg-black">
        <div className="aspect-video w-full">
          <iframe
            src={`https://iframe.videodelivery.net/${block.streamUid}`}
            title="Video"
            className="h-full w-full"
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    );
  }

  return null;
}

function PostImage({ src }: { src: string }) {
  const [error, setError] = useState(false);

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center rounded-2xl border-2 border-ink/10 bg-paper-deep">
        <div className="flex flex-col items-center gap-2 text-center">
          <span className="text-3xl">🖼️</span>
          <p className="text-sm text-ink-muted">Image couldn't load</p>
        </div>
      </div>
    );
  }

  return (
    <figure className="overflow-hidden rounded-2xl border-2 border-ink/10 bg-paper-deep">
      <img
        src={src}
        alt=""
        className="block w-full"
        onError={() => setError(true)}
        loading="lazy"
      />
    </figure>
  );
}
